<script setup lang="ts">
import type { AppMessageBoxTheme } from './AppMessageBox.model';

interface AppMessageBoxProps {
  messageType?: AppMessageBoxTheme;
  headerText?: string;
  text?: string;
  withBorder?: boolean;
}

const props = withDefaults(defineProps<AppMessageBoxProps>(), {
  messageType: 'neutral-20',
  withBorder: false,
});
</script>

<template>
  <div
    class="app-message-box"
    :class="{ [props.messageType]: true, 'with-border': props.withBorder }"
  >
    <div class="content">
      <slot name="left-icon" />
      <span class="app-message-box-text">
        <span v-if="props.headerText" class="header">
          {{ props.headerText }}
        </span>
        <span v-if="props.text">{{ props.text }}</span>
        <template v-else><slot name="text" /></template>
      </span>
      <slot name="right-icon" />
    </div>
    <slot name="link" />
  </div>
</template>

<style scoped lang="scss">
@use '../../styles/fonts' as *;

.app-message-box {
  display: flex;
  flex-flow: column;
  gap: var(--spacing-16);
  align-items: flex-start;

  padding: var(--spacing-12);
  border-radius: var(--border-radius-8);
  cursor: default;

  .content {
    display: flex;
    align-items: center;
    gap: var(--spacing-8);

    .app-message-box-text {
      .header {
        @include font-body-l-bold;
        display: block;
      }

      align-self: center;
      color: var(--color-neutral-70);
      @include font-body-m-light;
    }
  }

  &.brand-lagoon {
    background-color: var(--color-brand-lagoon);
  }

  &.brand-lilac {
    background-color: var(--color-brand-lilac);
  }

  &.brand-summer {
    background-color: var(--color-brand-summer);
  }

  &.neutral-20 {
    background-color: var(--color-neutral-20);
  }

  &.error-light {
    background-color: var(--color-error-light);

    &.with-border {
      border-left: 5px solid var(--color-error-dark);
    }
  }
}
</style>
